import { Box, ResponsiveContext } from 'grommet';
import React, { useContext, useState } from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { handleErrors } from '../../../utils/errors';
import { useBackgroundSWR } from '../../../utils/useBackgroundSWR';
import { NotificationsContext } from '../../NotificationsProvider';
import ChartPanel from '../../charts/ChartPanel';
import DashboardStrip from './DashboardStrip';
import TopCustomers from '../../charts/TopCustomers';
import FollowUpFirst from '../../charts/FollowUpFirst';
import GoneQuiet from '../../charts/GoneQuiet';
import { openNotificationAction } from '../../NotificationBanner';
import { IconBell, IconFileAlert, IconLink, IconMailX, IconUserCheck } from '@tabler/icons-react';
import TaskList from '../../TaskList';
import { plural } from '../../../utils/string';
import {useSession} from "next-auth/react";
import styled from 'styled-components';

const CardGrid = styled.div`
    display: grid;
    gap: 20px;
    margin: 12px 0 20px;
    grid-auto-rows: min-content;
    /* Follows the space the sidebar leaves, not the viewport */
    grid-template-columns: repeat(auto-fit, minmax(min(340px, 100%), 1fr));
`;

// Takes no gap when the card has nothing to show
const FullWidth = styled.div`
    grid-column: 1 / -1;

    &:empty { display: none; }
`;

// The first few failed invoices under their row, and how many more the row opens
const MAX_DETAILS = 3;
const detailLines = (details) => {
    if (!details?.length) return undefined;
    const lines = details.slice(0, MAX_DETAILS).map((detail) => `${detail.order_identifier}${detail.customer_name ? ` (${detail.customer_name})` : ''}: ${detail.reason}`);
    if (details.length > MAX_DETAILS) lines.push(`and ${details.length - MAX_DETAILS} more`);
    return lines;
};

// The two notifications the dashboard gets, written like the other rows. Any other kind keeps the
// backend's own sentence.
const notificationTask = (notification) => {
    const count = notification.params?.count;
    if (notification.type === 'TotalFailedInvoiceNotification' && count) {
        return { icon: IconFileAlert, lead: plural(count, 'invoice', 'invoices'), text: 'to complete', action: 'Fix' };
    }
    // The backend counts the invoices whose email bounced, not the emails
    if (notification.type === 'TotalBouncedEmailNotification' && count) {
        return { icon: IconMailX, lead: plural(count, 'undelivered invoice', 'undelivered invoices'), text: 'to check', action: 'Review' };
    }
    return { icon: IconBell, text: notification.message, action: 'Review' };
};

const Dashboard = ({ extraTasks = [] }) => {
    const [reportDate, setReportDate] = useState();
    const {data: session} = useSession();
    const {company} = session || {};
    const {currency} = company || {};
    const notificationCtx = useContext(NotificationsContext);
    const router = useRouter();

    const fetcher = (url) => fetch(url)
        .then(handleErrors)
        .catch(notificationCtx.showNotification);

    // fetch balances to display in dashboard aging balance graph
    const {data: dashboardData, error: dashboardError} = useSWR(
        `/api/company/dashboard?${reportDate ? `report_date=${reportDate.toISOString()}&` : ''}`,
        fetcher,
        {
            revalidateOnFocus: false,
            // Changing the "As of" date keeps the last chart up instead of flashing a skeleton
            keepPreviousData: true,
        }
    );

    const {data: unknownPaymentsData, error: unknownPaymentsError} = useBackgroundSWR(
        `/api/company/unknown-payments`,
        {
            revalidateOnFocus: false,
        }
    );

    const { data: customerSummaryData, error: customerSummaryDataError} = useBackgroundSWR(
        `/api/company/customer-summary`,
        {
            revalidateOnFocus: false,
        }
    );
    const pendingApplications = customerSummaryData?.total_applications ?? 0;

    const {data: notifications, error: notificationsError} = useBackgroundSWR(
        `/api/company/notifications`,
        {
            revalidateOnFocus: false,
        }
    );

    const {data: monthlySales, error: monthlySalesError} = useSWR(
        `/api/reports/monthly-sales`,
        fetcher,
        {
            revalidateOnFocus: false,
        }
    );

    const {data: averagePaymentPeriod, error: averagePaymentPeriodError} = useSWR(
        `/api/reports/average-payment`,
        fetcher,
        {
            revalidateOnFocus: false,
        }
    );

    const isPhone = useContext(ResponsiveContext) === 'small';
    // Five customers each, three on a phone where the cards stack
    const listRows = isPhone ? 3 : 5;
    const lists = (
        <>
            {/* A response without data has no period to ask about */}
            {monthlySales?.data?.length > 0 && (
                <TopCustomers
                    months={monthlySales.data.map((row) => row.month)}
                    currency={currency}
                    hasActivity={monthlySales.data.some((row) => row.sales_amount || row.payment_amount)}
                    rows={listRows}
                />
            )}
            <GoneQuiet currency={currency} rows={listRows} />
            <FollowUpFirst currency={currency} rows={listRows} />
        </>
    );

    const unknownCount = unknownPaymentsData?.unknown_payments_count || 0;
    // What used to be a banner each, as rows in one card
    const tasks = [
        ...extraTasks,
        unknownCount > 0 && {
            key: 'unknown_payments',
            icon: IconLink,
            lead: plural(unknownCount, 'payment', 'payments'),
            text: 'to link',
            action: 'Link',
            // Cash that came in but isn't applied to an invoice yet
            attention: true,
            onClick: () => router.push('/transactions'),
        },
        pendingApplications > 0 && {
            key: 'applications',
            icon: IconUserCheck,
            lead: plural(pendingApplications, 'application', 'applications'),
            text: 'to review',
            action: 'Review',
            onClick: () => router.push('/customers/applications'),
        },
        ...(notifications?.notifications || []).map((notification) => ({
            key: `notification_${notification.id}`,
            ...notificationTask(notification),
            details: detailLines(notification.details),
            // Both kinds the backend sends are invoices that never reached the customer
            attention: true,
            onClick: () => openNotificationAction(notification.action, router),
        })),
    ].filter(Boolean);

    return (
        <Box width={{max: '2020px'}}>
            {/* Nothing to do, no card and no gap where it would be */}
            {tasks.length > 0 && (
                <CardGrid>
                    <FullWidth>
                        <TaskList tasks={tasks} section="dashboard_tasks" />
                    </FullWidth>
                </CardGrid>
            )}
            <CardGrid>
                <FullWidth>
                    <DashboardStrip
                        dashboardData={dashboardData}
                        monthlySales={monthlySales}
                        averagePaymentPeriod={averagePaymentPeriod}
                        currency={currency}
                        reportDate={reportDate}
                        setReportDate={setReportDate}
                    />
                </FullWidth>
                <FullWidth>
                    {isPhone ? (
                        // On a phone, three short cards of their own rather than one long frame. A CSS
                        // gap, since Grommet's gap sees the fragment as one child.
                        <Box style={{ rowGap: '20px' }}>{lists}</Box>
                    ) : (
                        // One frame for the three, so they line up however long each one runs
                        <ChartPanel>{lists}</ChartPanel>
                    )}
                </FullWidth>
            </CardGrid>
        </Box>
    );
}


export default Dashboard;
